import { createFileRoute, Link, useSearch } from '@tanstack/react-router'
import { Check, LoaderCircle, XCircle } from 'lucide-react'
import { useEffect, useRef } from 'react'
import { z } from 'zod'

import { Button } from '@/components/ui/button'
import { useVerifyEmailMutation } from '@/features/auth/api'
import { AuthLayout } from '@/features/auth/components/auth-layout'

const searchSchema = z.object({
  token: z.string().optional(),
})

export const Route = createFileRoute('/verify-email')({
  validateSearch: searchSchema,
  component: VerifyEmailPage,
})

function VerifyEmailPage() {
  const { token } = useSearch({ from: '/verify-email' })
  const mutation = useVerifyEmailMutation()
  const fired = useRef(false)

  // Auto-verify on mount when token is present. Guard with a ref so the
  // request only fires once per mount even under React 19 strict double-
  // render or fast reload.
  useEffect(() => {
    if (!token || fired.current) return
    fired.current = true
    mutation.mutate({ token })
  }, [token, mutation])

  if (!token) {
    return (
      <AuthLayout>
        <div className="flex flex-col gap-6">
          <div className="flex flex-col gap-1">
            <h1 className="text-2xl font-semibold tracking-tight">
              Invalid link
            </h1>
            <p className="text-sm text-muted-foreground">
              This verification link is missing its token. Request a new one
              from your account settings.
            </p>
          </div>
          <Button asChild>
            <Link to="/settings">Open settings</Link>
          </Button>
        </div>
      </AuthLayout>
    )
  }

  return (
    <AuthLayout>
      <div className="flex flex-col items-center gap-5 text-center">
        {mutation.isPending && (
          <>
            <LoaderCircle className="size-8 animate-spin text-muted-foreground" />
            <p className="text-sm text-muted-foreground">
              Verifying your email…
            </p>
          </>
        )}

        {mutation.isSuccess && (
          <>
            <div className="flex size-12 items-center justify-center rounded-full bg-success/15 text-success">
              <Check className="size-6" />
            </div>
            <h1 className="text-2xl font-semibold tracking-tight">
              Email verified
            </h1>
            <p className="text-sm text-muted-foreground">
              Thanks for confirming. You're all set.
            </p>
            <Button asChild className="mt-2">
              <Link to="/dashboard">Go to dashboard</Link>
            </Button>
          </>
        )}

        {mutation.isError && (
          <>
            <div className="flex size-12 items-center justify-center rounded-full bg-destructive/10 text-destructive">
              <XCircle className="size-6" />
            </div>
            <h1 className="text-2xl font-semibold tracking-tight">
              Verification failed
            </h1>
            <p className="text-sm text-muted-foreground">
              This link is invalid or has expired. Open settings to send
              yourself a new one.
            </p>
            <Button asChild className="mt-2">
              <Link to="/settings">Open settings</Link>
            </Button>
          </>
        )}
      </div>
    </AuthLayout>
  )
}
